import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff, GripVertical } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';
import { resolveMediaUrl } from '../../utils/mediaUrl';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminSlidersList = () => {
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    fetchSliders();
  }, []);

  const fetchSliders = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API}/sliders/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSliders(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch sliders',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this slider?')) return;

    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API}/sliders/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: 'Success',
        description: 'Slider deleted successfully'
      });
      fetchSliders();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete slider',
        variant: 'destructive'
      });
    }
  };

  const handleToggle = async (id) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.patch(`${API}/sliders/${id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: 'Success',
        description: response.data.message
      });
      fetchSliders();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle slider status',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-gray-600">Loading sliders...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Homepage Sliders</h1>
          <p className="text-sm text-gray-600 mt-1">Manage homepage hero slider content</p>
        </div>
        <Button
          onClick={() => navigate('/admin/sliders/new')}
          className="bg-[#be1f1a] hover:bg-[#a71b17]"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Slider
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Order
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Preview
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Title
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sliders.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                  No sliders found. Create your first slider to get started.
                </td>
              </tr>
            ) : (
              sliders.map((slider) => {
                // Get first available version for display
                const firstVersion = slider.versions && slider.versions.length > 0 
                  ? slider.versions.find(v => v.heading) || slider.versions[0]
                  : null;
                const displayHeading = firstVersion?.heading || slider.slug || 'Untitled';
                const displaySubheading = firstVersion?.subheading || '';
                
                return (
                  <tr key={slider.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <GripVertical className="h-4 w-4 text-gray-400 mr-2" />
                        {slider.order}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {slider.image ? (
                        <img
                          src={resolveMediaUrl(slider.image)}
                          alt={displayHeading}
                          className="h-16 w-28 object-cover rounded"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="h-16 w-28 bg-gray-200 rounded flex items-center justify-center">
                          <span className="text-xs text-gray-400">No image</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{displayHeading}</div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">{displaySubheading}</div>
                      <div className="text-xs text-gray-400 mt-1">{slider.slug}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          slider.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {slider.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleToggle(slider.id)}
                          className="text-gray-600 hover:text-gray-900"
                          title={slider.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {slider.isActive ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => navigate(`/admin/sliders/edit/${slider.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(slider.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminSlidersList;
